import React, { useMemo, useState } from 'react';
import { Dropdown, Form, Button } from 'react-bootstrap';
import { FaFilter, FaTimes } from 'react-icons/fa';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
    useGetUniqueColorsQuery,
    useGetUniqueCharactersQuery,
    useGetProductTypesQuery,
} from '../slices/productsApiSlice';

const FilterDropdown = ({ defaultExpanded = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [show, setShow] = useState(defaultExpanded);

    // Fetch dynamic filter options
    const { data: colors = [], isLoading: colorsLoading } = useGetUniqueColorsQuery();
    const { data: characters = [], isLoading: charactersLoading } = useGetUniqueCharactersQuery();
    const { data: productTypesData = [], isLoading: productTypesLoading } = useGetProductTypesQuery();

    // Extract + dedupe product types
    const productTypes = useMemo(() => {
        const types = (productTypesData || []).map((p) => p.productType).filter(Boolean);
        return Array.from(new Set(types));
    }, [productTypesData]);

    // Check if we're on landing page
    const isLandingPage = location.pathname === '/';

    // Get current filters from URL
    const currentFilters = {
        productType: searchParams.getAll('productType'),
        minPrice: searchParams.get('minPrice') || '',
        maxPrice: searchParams.get('maxPrice') || '',
        inStock: searchParams.get('inStock') === 'true',
        onSale: searchParams.get('onSale') === 'true',
        color: searchParams.getAll('color'),
        character: searchParams.getAll('character'),
    };

    // Navigate to /search if on landing page, otherwise just update params
    const updateFilters = (newParams) => {
        if (isLandingPage) {
            navigate(`/search?${newParams.toString()}`);
        } else {
            setSearchParams(newParams);
        }

        // Keep dropdown open after filter change (only relevant for dropdown mode)
        if (!defaultExpanded) setShow(true);
    };

    // Handle Product Type checkbox changes
    const handleProductTypeChange = (type) => {
        const current = currentFilters.productType;
        const updated = current.includes(type) ? current.filter((t) => t !== type) : [...current, type];

        const newParams = new URLSearchParams(searchParams);
        newParams.delete('productType');
        updated.forEach((t) => newParams.append('productType', t));
        updateFilters(newParams);
    };

    // Handle Color checkbox changes
    const handleColorChange = (selectedColor) => {
        const current = currentFilters.color;
        const updated = current.includes(selectedColor)
            ? current.filter((c) => c !== selectedColor)
            : [...current, selectedColor];

        const newParams = new URLSearchParams(searchParams);
        newParams.delete('color');
        updated.forEach((c) => newParams.append('color', c));
        updateFilters(newParams);
    };

    // Handle Character checkbox changes
    const handleCharacterChange = (selectedCharacter) => {
        const current = currentFilters.character;
        const updated = current.includes(selectedCharacter)
            ? current.filter((ch) => ch !== selectedCharacter)
            : [...current, selectedCharacter];

        const newParams = new URLSearchParams(searchParams);
        newParams.delete('character');
        updated.forEach((ch) => newParams.append('character', ch));
        updateFilters(newParams);
    };

    // Handle price range
    const handlePriceChange = (min, max) => {
        const newParams = new URLSearchParams(searchParams);

        if (min) newParams.set('minPrice', min);
        else newParams.delete('minPrice');

        if (max) newParams.set('maxPrice', max);
        else newParams.delete('maxPrice');

        updateFilters(newParams);
    };

    // Handle toggles
    const handleToggle = (key, value) => {
        const newParams = new URLSearchParams(searchParams);

        if (value) newParams.set(key, 'true');
        else newParams.delete(key);

        updateFilters(newParams);
    };

    // Clear all filters
    const clearAllFilters = () => {
        if (isLandingPage) navigate('/');
        else setSearchParams(new URLSearchParams());
    };

    // Count active filters
    const activeFilterCount =
        currentFilters.productType.length +
        (currentFilters.minPrice ? 1 : 0) +
        (currentFilters.maxPrice ? 1 : 0) +
        (currentFilters.inStock ? 1 : 0) +
        (currentFilters.onSale ? 1 : 0) +
        currentFilters.color.length +
        currentFilters.character.length;

    const FilterContent = () => (
        <>
            <div
                style={{
                    marginBottom: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <h6 style={{ margin: 0, fontWeight: 'bold' }}>Filters</h6>
                {activeFilterCount > 0 && (
                    <Button
                        variant="link"
                        size="sm"
                        onClick={clearAllFilters}
                        style={{ padding: 0, color: '#FD7014', textDecoration: 'none' }}
                    >
                        <FaTimes /> Clear All
                    </Button>
                )}
            </div>

            <hr />

            {/* Product Type */}
            <div style={{ marginBottom: '1.5rem' }}>
                <h6 style={{ fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                    Product Type
                </h6>

                {productTypesLoading ? (
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>Loading product types...</div>
                ) : productTypes.length > 0 ? (
                    productTypes.map((type) => (
                        <Form.Check
                            key={type}
                            type="checkbox"
                            id={`filter-${type}-${defaultExpanded ? 'sidebar' : 'dropdown'}`}
                            label={type}
                            checked={currentFilters.productType.includes(type)}
                            onChange={() => handleProductTypeChange(type)}
                            style={{ marginBottom: '0.5rem' }}
                        />
                    ))
                ) : (
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>No product types available</div>
                )}
            </div>

            <hr />

            {/* Color */}
            <div style={{ marginBottom: '1.5rem' }}>
                <h6 style={{ fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '0.75rem' }}>Color</h6>

                {colorsLoading ? (
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>Loading colors...</div>
                ) : colors.length > 0 ? (
                    <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                        {colors.map((color) => (
                            <Form.Check
                                key={color}
                                type="checkbox"
                                id={`filter-color-${color}-${defaultExpanded ? 'sidebar' : 'dropdown'}`}
                                label={color}
                                checked={currentFilters.color.includes(color)}
                                onChange={() => handleColorChange(color)}
                                style={{ marginBottom: '0.5rem' }}
                            />
                        ))}
                    </div>
                ) : (
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>No colors available</div>
                )}
            </div>

            <hr />

            {/* Character/Brand */}
            <div style={{ marginBottom: '1.5rem' }}>
                <h6 style={{ fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                    Character/Brand
                </h6>

                {charactersLoading ? (
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>Loading characters...</div>
                ) : characters.length > 0 ? (
                    <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                        {characters.map((character) => (
                            <Form.Check
                                key={character}
                                type="checkbox"
                                id={`filter-character-${character}-${defaultExpanded ? 'sidebar' : 'dropdown'}`}
                                label={character}
                                checked={currentFilters.character.includes(character)}
                                onChange={() => handleCharacterChange(character)}
                                style={{ marginBottom: '0.5rem' }}
                            />
                        ))}
                    </div>
                ) : (
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>No characters available</div>
                )}
            </div>

            <hr />

            {/* Price Range */}
            <div style={{ marginBottom: '1.5rem' }}>
                <h6 style={{ fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                    Price Range
                </h6>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.5rem' }}>
                    <Button
                        variant={!currentFilters.minPrice && !currentFilters.maxPrice ? 'primary' : 'outline-secondary'}
                        size="sm"
                        onClick={() => handlePriceChange('', '')}
                    >
                        Any Price
                    </Button>

                    <Button
                        variant={currentFilters.maxPrice === '20' ? 'primary' : 'outline-secondary'}
                        size="sm"
                        onClick={() => handlePriceChange('0', '20')}
                    >
                        Under £20
                    </Button>

                    <Button
                        variant={
                            currentFilters.minPrice === '20' && currentFilters.maxPrice === '50'
                                ? 'primary'
                                : 'outline-secondary'
                        }
                        size="sm"
                        onClick={() => handlePriceChange('20', '50')}
                    >
                        £20 - £50
                    </Button>

                    <Button
                        variant={currentFilters.minPrice === '50' ? 'primary' : 'outline-secondary'}
                        size="sm"
                        onClick={() => handlePriceChange('50', '')}
                    >
                        Over £50
                    </Button>
                </div>
            </div>

            <hr />

            {/* Availability */}
            <div>
                <h6 style={{ fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                    Availability
                </h6>

                <Form.Check
                    type="checkbox"
                    id={`filter-inStock-${defaultExpanded ? 'sidebar' : 'dropdown'}`}
                    label="In Stock Only"
                    checked={currentFilters.inStock}
                    onChange={(e) => handleToggle('inStock', e.target.checked)}
                    style={{ marginBottom: '0.5rem' }}
                />

                <Form.Check
                    type="checkbox"
                    id={`filter-onSale-${defaultExpanded ? 'sidebar' : 'dropdown'}`}
                    label="On Sale"
                    checked={currentFilters.onSale}
                    onChange={(e) => handleToggle('onSale', e.target.checked)}
                />
            </div>
        </>
    );

    // Sidebar mode
    if (defaultExpanded) {
        return <FilterContent />;
    }

    // Dropdown mode
    return (
        <>
            <style>{`
    .filter-dropdown-menu-custom {
        min-width: 320px;
        max-height: 80vh;
        overflow-y: auto;
        padding: 1rem;
        margin-bottom: 1rem;
    }

    @media (max-width: 768px) {
        .filter-dropdown-menu-custom {
            min-width: 85vw !important;
            max-width: 85vw !important;
            max-height: 60vh !important;
            position: fixed !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
            right: auto !important;
            top: 160px !important;
            margin-bottom: 4rem !important;
        }
    }
`}</style>

            <Dropdown show={show} onToggle={(nextShow) => setShow(nextShow)} align="end">
                <Dropdown.Toggle
                    as="button"
                    className="btn btn-link"
                    style={{
                        textDecoration: 'none',
                        color: '#000',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        position: 'relative',
                        padding: '0',
                        cursor: 'pointer',
                    }}
                >
                    <FaFilter /> Filter
                    {activeFilterCount > 0 && (
                        <span
                            style={{
                                position: 'absolute',
                                top: '-5px',
                                right: '-5px',
                                background: '#FD7014',
                                color: 'white',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                            }}
                        >
                            {activeFilterCount}
                        </span>
                    )}
                </Dropdown.Toggle>

                <Dropdown.Menu
                    className="filter-dropdown-menu-custom"
                    onClick={(e) => e.stopPropagation()}
                >
                    <FilterContent />
                </Dropdown.Menu>
            </Dropdown>
        </>
    );
};

export default FilterDropdown;